import { ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Collapse,
  Divider,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  MenuItem,
  Text,
} from '@chakra-ui/react';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import { startCase } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  BuildADashTrackingEvents,
  FeatureFlag,
  PageTitles,
} from '@revelio/core';
import { Badge, BadgeColor } from '@revelio/ui';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import {
  type Page,
  PageType,
} from '../../../CustomDash/components/PageConfigProvider/types';
import { usePageManager } from '../../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import {
  FilterId,
  type PrimaryFilterId,
} from '../../../CustomDash/filters/types/filters';
import { buildPathFromPage } from '../../../CustomDash/utils/routing-helpers';
import { buildMyPagesLinkSearch } from './my-pages-filter-search';
import { SubNavItem } from './SubNavItem';

const getPageUnitLabel = (page: Page): string | null => {
  const filterId = page.tabs?.[0]?.tabContent?.primaryFilter?.id;
  if (!filterId) return null;
  return startCase(filterId.toLowerCase());
};

const UNIT_BADGE_STYLE: Record<
  PrimaryFilterId,
  { backgroundColor: string; color: string }
> = {
  [FilterId.COMPANY]: {
    backgroundColor: 'var(--ui-light-blue-50)',
    color: 'var(--ui-light-blue-500)',
  },
  [FilterId.GEOGRAPHY]: {
    backgroundColor: 'var(--ui-purple-50)',
    color: 'var(--ui-purple-500)',
  },
  [FilterId.ROLE]: {
    backgroundColor: 'var(--ui-green-50)',
    color: 'var(--ui-green-500)',
  },
};

const getPageUnitBadgeStyle = (page: Page) => {
  const filterId = page.tabs?.[0]?.tabContent?.primaryFilter?.id;
  return filterId ? UNIT_BADGE_STYLE[filterId] : UNIT_BADGE_STYLE.GEOGRAPHY;
};

export interface CustomDashSubNavProps {
  parentName: string;
  primaryFilter?: PrimaryFilterId;
  onLinkClick?: () => void;
  isOpen: boolean;
}

export const CustomDashSubNav = ({
  parentName: _parentName,
  primaryFilter,
  onLinkClick,
  isOpen,
}: CustomDashSubNavProps) => {
  const customDashNavEnabled = useUnleashFlag(
    FeatureFlag.CustomDashViewEditToggle
  );
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  const isReports =
    _parentName === PageTitles.DELIVERABLES ||
    _parentName === PageTitles.REPORTS_NAV;

  const { pages, deletePage, clonePage, updatePage, editPage } =
    usePageManager();
  const location = useLocation();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    if (!isOpen) {
      setSearchMode(false);
      setSearchTerm('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchMode) {
      setIsCollapsed(false);
    }
  }, [searchMode]);

  if (!customDashNavEnabled) {
    return null;
  }

  // In the new dashboard layout, the Pages popover in the Header replaces the
  // sidebar "My Pages" list entirely.
  if (isNewDashboardLayout) {
    return null;
  }

  // In the new dashboard layout, Company/Geography/Role all live under a
  // single DASHBOARD_NAV, so My Pages should surface non-report pages from
  // every unit type rather than filtering by the (hardcoded) primary filter.
  const isUnifiedDashboardNav =
    isNewDashboardLayout && _parentName === PageTitles.DASHBOARD_NAV;

  const basePages = pages.filter(
    (p) =>
      (isUnifiedDashboardNav && p.type !== PageType.REPORT) ||
      (p.tabs?.[0]?.tabContent?.primaryFilter?.id === primaryFilter &&
        p.type !== PageType.REPORT) ||
      (_parentName === 'Data' && p.type === PageType.REPORT)
  );

  const filteredPages = basePages.filter((p) =>
    searchTerm ? p.name.toLowerCase().includes(searchTerm.toLowerCase()) : true
  );

  function highlightMatch(text: string, term: string) {
    if (!term) return text;
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <Box as="span" fontWeight="bold" key={i}>
              {part}
            </Box>
          ) : (
            part
          )
        )}
      </>
    );
  }

  if (!basePages.length) {
    return null;
  }

  // Hide My Reports from side nav
  if (isReports) {
    return null;
  }

  return (
    <Flex direction="column" width="100%" minH={0} flex="1">
      <Flex
        align="center"
        justify="space-between"
        pb="2"
        width="100%"
        overflowX="hidden"
        pt="2px"
        flexShrink={0}
      >
        {searchMode ? (
          <Input
            size="sm"
            placeholder="Search pages"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setSearchMode(false);
                setSearchTerm('');
              }
            }}
            autoFocus
            borderRadius="md"
            ml="1px"
            mr="2"
          />
        ) : (
          <Heading color="text.primary" size="md">
            My Pages
          </Heading>
        )}

        <Flex ml="auto" align="center" gap="1">
          <IconButton
            aria-label={searchMode ? 'Close search' : 'Search pages'}
            variant="ghost"
            size="sm"
            icon={
              searchMode ? (
                <IconCrossLarge size={16} />
              ) : (
                <IconMagnifyingGlass size={16} />
              )
            }
            onClick={() => {
              if (searchMode) {
                setSearchTerm('');
              }
              setSearchMode(!searchMode);
            }}
            color="text.primary"
            _hover={{ bg: 'gray.100', color: 'text.primary' }}
            _active={{ bg: 'gray.200' }}
          />

          {!searchMode && (
            <Icon
              as={ChevronUpIcon}
              boxSize={4}
              cursor="pointer"
              transition="transform 0.2s"
              transform={isCollapsed ? 'rotate(-180deg)' : undefined}
              onClick={() => setIsCollapsed(!isCollapsed)}
              _hover={{ opacity: 0.8 }}
            />
          )}
        </Flex>
      </Flex>

      <Collapse
        in={!isCollapsed}
        animateOpacity
        style={{
          width: '100%',
          alignSelf: 'stretch',
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          overflowY="auto"
          flex="1"
          minH={0}
          sx={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#E3E3E3 transparent',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#E3E3E3',
              borderRadius: '10px',
            },
          }}
        >
          {filteredPages.length === 0 && searchTerm ? (
            <Flex
              px="3"
              py="2"
              justifyContent="center"
              alignItems="center"
              mt="8px"
            >
              <Text color="gray.500" fontSize="md">
                No pages found
              </Text>
            </Flex>
          ) : (
            filteredPages.map((page) => {
              const unitLabel = getPageUnitLabel(page);
              const unitBadgeStyle = getPageUnitBadgeStyle(page);
              const showUnitTag = isNewDashboardLayout && unitLabel;
              return (
                <SubNavItem
                  key={page.id}
                  data-testid={`custom-dash-subnav-${page.id}`}
                  to={buildPathFromPage(page, {
                    search: buildMyPagesLinkSearch(location.search),
                  })}
                  editing={editingId === page.id}
                  editableValue={page.name}
                  onClick={onLinkClick}
                  onRename={(val) => {
                    if (val && val !== page.name) {
                      updatePage(page.id, { ...page, name: val });
                    }
                    setEditingId(null);
                  }}
                  menuContent={
                    <>
                      <MenuItem
                        onClick={() => {
                          onLinkClick?.();
                          editPage(page.id);
                          mixpanel.track(BuildADashTrackingEvents.EDIT_PAGE, {
                            page_id: page.id,
                          });
                        }}
                      >
                        Edit Page
                      </MenuItem>
                      <MenuItem onClick={() => setEditingId(page.id)}>
                        Rename
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          clonePage(page.id);
                          mixpanel.track(BuildADashTrackingEvents.CLONE_PAGE, {
                            page_id: page.id,
                          });
                        }}
                      >
                        Make a Copy
                      </MenuItem>
                      <Divider />
                      <MenuItem
                        onClick={() => {
                          deletePage(page.id);
                          mixpanel.track(BuildADashTrackingEvents.DELETE_PAGE, {
                            page_id: page.id,
                          });
                        }}
                      >
                        Delete
                      </MenuItem>
                    </>
                  }
                >
                  <Flex align="center" gap={2} width="100%">
                    <Box
                      flex="1"
                      minWidth={0}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {highlightMatch(page.name, searchTerm)}
                    </Box>
                    {showUnitTag && (
                      <Box
                        marginLeft="auto"
                        marginRight={-2}
                        display="none"
                        _groupHover={{ display: 'inline-flex' }}
                      >
                        <Badge
                          color={BadgeColor.LIGHT_BLUE}
                          variant="subtle"
                          style={unitBadgeStyle}
                          data-testid={`custom-dash-subnav-${page.id}-unit-tag`}
                        >
                          {unitLabel}
                        </Badge>
                      </Box>
                    )}
                  </Flex>
                </SubNavItem>
              );
            })
          )}
        </Box>
      </Collapse>
    </Flex>
  );
};

export default CustomDashSubNav;
